import React, {
    useCallback,
    useEffect,
    useMemo, useRef, useState,
} from 'react'
import styles from 'styles/components/pages/order-quotes/[id]/quotation/quotation.module.scss'
import Card from 'components/containers/card'
import useTranslate from 'helpers/hooks/useTranslate'
import {
    ConstrainMode,
    DetailsListLayoutMode,
    SelectionMode, ShimmeredDetailsList,
    DialogType,
    Dialog,
    MessageBar,
    MessageBarType,
    TextField,
    Label,
    DialogFooter,
    DefaultButton,
    PrimaryButton,
    IconButton,
} from '@fluentui/react'
import Param from 'requests/objects/param'
import Status from 'types/status'
import User from 'requests/objects/user'
import OrderQuote from 'requests/objects/order-quote'
import OrderQuotesHandler from 'requests/handlers/orderQuotesHandler'
import OrderQuotePricing, { ErrorOrderPricing } from 'requests/objects/order-quote-pricing'
import { Columns } from 'react-bulma-components'
import parseJson from 'helpers/methods/parseJson'
import FilteredVirtualCombobox from 'components/inputs/filteredVirtualCombobox'
import InvalidEntityError from 'requests/errors/invalidEntityError'
import CancelRequestError from 'requests/errors/cancelRequestError'
import UnauthorizedError from 'requests/errors/unauthorizedError'
import NotImplementedError from 'requests/errors/notImplementedError'
import formatPrice from 'helpers/methods/formatPrice'
import EWorkflowStep from 'types/orders/enums/workflowStep'
import { computeTotalHt, computeTotalTtc, getClientVat } from 'helpers/methods/priceCalculator'

/**
 * ConfirmationStep
 * @param {object} props Props
 * @param {object} props.lang Lang
 * @param {OrderQuote} props.item Item
 * @param {Param} props.param param
 * @param {(order: Partial<OrderQuote>) => void} props.onChange onChange
 * @param {(arg0: import('redux/slices/common').PayloadMessageBar) => void} props.setMessageBar setMessageBar
 * @param {OrderQuotesHandler} props.orderQuotesHandler orderQuotesHandler
 * @param {User} props.me me
 * @returns {JSX.Element} Element
 */
export default function Quotation({
    lang, item, param,
    onChange, setMessageBar, orderQuotesHandler, me,
}) {
    const { tKey, tObj } = useTranslate({ lang })
    const [status, setStatus] = useState(Status.IDLE)
    const [errorMessage, setErrorMessage] = useState('')
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [pricing, setPricing] = useState(new OrderQuotePricing())
    const [errorField, setErrorField] = useState(new ErrorOrderPricing())
    const [isKnownedService, setIsKnownService] = useState(true)

    const [costInput, setCostInput] = useState('')
    const [isDownloading, setIsDownloading] = useState(false)
    const [, setDownloadError] = useState('')

    /** @type {import('react').MutableRefObject<import('requests/apiHandler').RequestApi<OrderQuotePricing>>} */
    const pricingHandler = useRef(null)

    const canUpdate = item.currentWorkflowStepId === EWorkflowStep.QuoteCosting && me.isAdmin

    const upsertPricing = useCallback(async () => {
        setStatus(Status.PENDING)
        setMessageBar({ isDisplayed: false })
        setErrorMessage('')
        setErrorField(new ErrorOrderPricing())
        try {
            pricingHandler.current = pricing.orderQuotePricingId
                ? orderQuotesHandler.updateOrderQuotePricing(item.orderQuoteId, pricing)
                : orderQuotesHandler.createOrderQuotePricing(item.orderQuoteId, pricing)

            const newPricing = await pricingHandler.current.fetch()

            const orderQuotePricings = [...item.orderQuotePricings]
            if (pricing.orderQuotePricingId) {
                const index = orderQuotePricings.findIndex(x => x.orderQuotePricingId === newPricing.orderQuotePricingId)
                orderQuotePricings[index] = newPricing
            } else {
                orderQuotePricings.push(newPricing)
            }
            onChange({ ...item, orderQuotePricings })

            setIsModalOpen(false)
            setPricing(new OrderQuotePricing())
            setErrorField(new ErrorOrderPricing())
            setStatus(Status.RESOLVED)
            setMessageBar({ isDisplayed: true, message: tKey('serviceSaved'), type: MessageBarType.success })
        } catch (error) {
            setMessageBar({ isDisplayed: false })
            setStatus(Status.REJECTED)
            setErrorMessage(error?.message)

            switch (error?.constructor) {
                case CancelRequestError:
                case UnauthorizedError:
                case NotImplementedError:
                    break
                case InvalidEntityError:
                    setErrorField(/** @type {InvalidEntityError<ErrorOrderPricing>} */(error).errorField)
                    break
                default:
                    break
            }
        }
    }, [item, onChange, orderQuotesHandler, pricing, setMessageBar, tKey])

    const deletePricing = useCallback(orderQuotePricingId => {
        setStatus(Status.PENDING)
        setMessageBar({ isDisplayed: false })
        try {
            pricingHandler.current = orderQuotesHandler.deleteOrderQuotePricing(item.orderQuoteId, orderQuotePricingId)
            pricingHandler.current.fetch()
            const newPricings = item.orderQuotePricings.filter(x => x.orderQuotePricingId !== orderQuotePricingId)
            onChange({ ...item, orderQuotePricings: newPricings })

            setMessageBar({ isDisplayed: true, message: tKey('serviceHasBeenRemoved'), type: MessageBarType.success })
            setStatus(Status.RESOLVED)
        } catch (error) {
            setMessageBar({ isDisplayed: false })
            setStatus(Status.REJECTED)
            setErrorMessage(error.message)
        }
    }, [item, onChange, orderQuotesHandler, setMessageBar, tKey])

    const downloadQuote = useCallback(async () => {
        if (!item?.orderQuoteId)
            return

        setDownloadError('')
        setIsDownloading(true)
        setMessageBar({ isDisplayed: false })

        try {
            const { blob, fileName } = await orderQuotesHandler.printOrderQuote(item.orderQuoteId, lang).fetch()

            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = fileName || `devis-${item.orderQuoteId}.pdf`
            document.body.appendChild(a)
            a.click()
            a.remove()
            window.URL.revokeObjectURL(url)
        } catch (error) {
            setDownloadError(error?.message || 'Erreur téléchargement devis')
            setMessageBar({
                isDisplayed: true,
                message: tKey('downloadFailed') || 'Téléchargement impossible.',
                type: MessageBarType.error,
            })
        } finally {
            setIsDownloading(false)
        }
    }, [item?.orderQuoteId, lang, orderQuotesHandler, setMessageBar, tKey])

    const columns = useMemo(() => /** @type {import('@fluentui/react').IColumn[]} */([
        {
            key: 'serviceLabel',
            name: tKey('serviceLabel'),
            fieldName: 'serviceLabel',
            minWidth: 200,
            maxWidth: 300,
            isResizable: true,
            onRender: (/** @type {OrderQuotePricing} */ data) => data.orderQuoteService?.name,
        },
        {
            key: 'unitPrice',
            name: tKey('unitPrice'),
            fieldName: 'unitPrice',
            minWidth: 80,
            maxWidth: 150,
            isResizable: true,
            onRender: (/** @type {OrderQuotePricing} */ data) => formatPrice(data.costExcludingVat),
        },
        {
            key: 'quantity',
            name: tKey('quantity'),
            fieldName: 'quantity',
            minWidth: 80,
            maxWidth: 150,
            isResizable: true,
            onRender: (/** @type {OrderQuotePricing} */ data) => data.quantity?.toString(),
        },
        {
            key: 'total',
            name: tKey('total'),
            fieldName: 'total',
            minWidth: 80,
            maxWidth: 150,
            isResizable: true,
            onRender: (/** @type {OrderQuotePricing} */ data) => formatPrice(data.costExcludingVat * data.quantity),
        },
        {
            key: 'vat',
            name: tKey('vat'),
            fieldName: 'vat',
            minWidth: 80,
            maxWidth: 150,
            isResizable: true,
            onRender: (/** @type {OrderQuotePricing} */ data) => `${data.vat?.toString()} %`,
        },

        {
            key: 'totalPrice',
            name: tKey('totalPrice'),
            fieldName: 'totalPrice',
            minWidth: 200,
            maxWidth: 300,
            isResizable: true,
            onRender: (/** @type {OrderQuotePricing} */ data) => formatPrice(
                (data.costExcludingVat * data.quantity) + ((data.costExcludingVat * data.quantity * data.vat) / 100),
            ),
        },
        {
            key: 'actions',
            minWidth: 100,
            maxWidth: 100,
            isResizable: false,
            // eslint-disable-next-line react/no-unstable-nested-components
            onRender: (/** @type {OrderQuotePricing} */ data) => {
                if (status === Status.PENDING || !canUpdate)
                    return null

                return (
                    <div className={styles['action-buttons']}>
                        <IconButton
                            iconProps={{ iconName: 'Edit' }}
                            onClick={() => {
                                setPricing(data)
                                setCostInput(
                                    data.costExcludingVat !== null && data.costExcludingVat !== undefined
                                        ? data.costExcludingVat.toString()
                                        : '',
                                )
                                setIsModalOpen(true)
                                setErrorField(new ErrorOrderPricing())
                            }}
                        />
                        <IconButton
                            iconProps={{ iconName: 'Delete' }}
                            onClick={() => {
                                deletePricing(data.orderQuotePricingId)
                            }}
                        />
                    </div>
                )
            },

        },
    ]), [canUpdate, deletePricing, status, tKey])

    useEffect(() => () => {
        pricingHandler?.current?.cancel()
    }, [])

    const totalTtcPreview = computeTotalTtc([
        {
            costExcludingVat: pricing.costExcludingVat,
            quantity: pricing.quantity,
            vat: getClientVat(item.client),
        },
    ])

    const [formError, setFormError] = useState(null)
    const isQuoteTotalOutOfRange = totalTtcPreview < 1 || totalTtcPreview > 9_999_999.99
    if (!isQuoteTotalOutOfRange && formError !== null)
        setFormError(null)

    return (
        <div className={styles.quotation}>
            <Card
                hasDividerMargin={false}
                className={styles['quotation-card']}
                title={tKey('quotationSummary')}
                iconName="BulletedList2"
                headerComponent={canUpdate && (
                    <PrimaryButton
                        text={tKey('addService')}
                        onClick={() => {
                            setIsModalOpen(true)
                            setPricing(new OrderQuotePricing())
                            setErrorField(new ErrorOrderPricing())
                            setCostInput('')
                        }}
                    />
                )}
            >
                {(me.isAdmin
                    || me.isSuperAdmin
                    || [
                        EWorkflowStep.QuoteAccepted,
                        EWorkflowStep.QuoteConfirmation,
                        EWorkflowStep.QuoteRefused,
                    ].includes(item.currentWorkflowStepId)
                ) && (
                    <>
                        <ShimmeredDetailsList
                            items={item.orderQuotePricings}
                            columns={columns}
                            selectionMode={SelectionMode.none}
                            onShouldVirtualize={() => true}
                            layoutMode={DetailsListLayoutMode.justified}
                            constrainMode={ConstrainMode.unconstrained}
                        />
                        <br />
                        {item.orderQuotePricings.length > 0 && (
                            <div className={styles['quotation-summary']}>
                                <div>
                                    <Label>{`${tKey('totalHtEstimated')} :`}</Label>
                                    <span>
                                        {formatPrice(computeTotalHt(item.orderQuotePricings))}
                                    </span>
                                </div>
                                <div>
                                    <Label>{`${tKey('totalPriceEstimated')} :`}</Label>
                                    <span>
                                        {formatPrice(computeTotalTtc(item.orderQuotePricings))}
                                    </span>
                                </div>
                                {item.currentWorkflowStepId !== EWorkflowStep.QuoteCosting && (
                                    <div>
                                        <PrimaryButton
                                            text={tKey('download')}
                                            iconProps={{ iconName: 'Download' }}
                                            onClick={downloadQuote}
                                            disabled={isDownloading || status === Status.PENDING}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

            </Card>
            <Dialog
                hidden={!isModalOpen}
                dialogContentProps={{
                    type: DialogType.largeHeader,
                    title: pricing?.orderQuotePricingId ? tKey('updateService') : tKey('enterNewService'),
                }}
                modalProps={{
                    isBlocking: true,
                }}
                maxWidth="800px"
                minWidth="670px"
            >

                {formError && (
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        isMultiline={false}
                        onDismiss={() => setFormError(null)}
                    >
                        {formError}
                    </MessageBar>
                )}

                {errorMessage
                    && (
                        <MessageBar
                            messageBarType={MessageBarType.error}
                            isMultiline={false}
                            truncated
                            onDismiss={() => setErrorMessage(null)}
                        >
                            {tObj(errorMessage)}
                        </MessageBar>
                    )}
                {/* <form
                    onSubmit={ev => {
                        ev.preventDefault()
                        upsertPricing()
                    }}
                /> */}
                <br />
                <Columns>
                    <Columns.Column size="one-third">
                        <Label>{`${tKey('serviceLabel')} :`}</Label>
                    </Columns.Column>
                    <Columns.Column size="one-third">
                        {
                            isKnownedService
                                ? (
                                    <FilteredVirtualCombobox
                                        placeholder={tKey('label')}
                                        options={param.orderQuoteServices}
                                        selectedKey={pricing.orderQuoteServiceId}
                                        onChange={(ev, option) => setPricing(prevState => ({ ...prevState, orderQuoteServiceId: option.key }))}
                                        disabled={status === Status.PENDING}
                                        errorMessage={tObj(parseJson(errorField.orderQuoteServiceId))}
                                    />
                                )
                                : (
                                    <TextField
                                        placeholder={tKey('label')}
                                        value={pricing.serviceName}
                                        onChange={(ev, newVal) => setPricing(prevState => ({ ...prevState, serviceName: newVal }))}
                                        disabled={status === Status.PENDING}
                                        errorMessage={tObj(parseJson(errorField.serviceName))}
                                    />
                                )
                        }
                    </Columns.Column>
                    <Columns.Column size="one-third">
                        <DefaultButton
                            text={tKey(isKnownedService ? 'addService' : 'cancel')}
                            onClick={() => setIsKnownService(!isKnownedService)}
                            disabled={status === Status.PENDING}
                        />
                    </Columns.Column>
                </Columns>
                <Columns>
                    <Columns.Column size="one-third">
                        <Label>{`${tKey('quantity')} :`}</Label>
                    </Columns.Column>
                    <Columns.Column size="one-third">
                        <TextField
                            placeholder={tKey('quantity')}
                            type="number"
                            value={pricing.quantity > 0 ? pricing.quantity.toString() : ''}
                            onChange={(ev, newVal) => setPricing(prevState => ({ ...prevState, quantity: +newVal }))}
                            disabled={status === Status.PENDING}
                            errorMessage={tObj(parseJson(errorField.quantity))}
                            min={1}
                        />
                    </Columns.Column>
                    <Columns.Column size="one-third" />
                </Columns>
                <Columns>
                    <Columns.Column size="one-third">
                        <Label>{`${tKey('priceExclTax')} :`}</Label>
                    </Columns.Column>
                    <Columns.Column size="one-third">
                        <TextField
                            placeholder={tKey('priceExclTax')}
                            type="text"
                            value={costInput}
                            onChange={(ev, newVal) => {
                                setCostInput(newVal)
                                setPricing(prev => ({
                                    ...prev,
                                    costExcludingVat: Number(newVal.replace(',', '.')) || 0,
                                }))
                            }}
                            disabled={status === Status.PENDING}
                            errorMessage={tObj(parseJson(errorField.costExcludingVat))}
                            suffix="€"
                        />
                    </Columns.Column>
                    <Columns.Column size="one-third" />
                </Columns>
                <br />
                <DialogFooter>
                    <DefaultButton
                        onClick={() => {
                            setIsModalOpen(false)
                            setPricing(new OrderQuotePricing())
                            setErrorField(new ErrorOrderPricing())
                            setCostInput('')
                        }}
                        text={tKey('cancel')}
                        disabled={status === Status.PENDING}
                    />
                    <PrimaryButton
                        text={tKey('save')}
                        disabled={status === Status.PENDING || !!formError}
                        onClick={e => {
                            e.preventDefault()

                            if (
                                (!pricing.serviceName && !pricing.orderQuoteServiceId)
                                || !pricing.costExcludingVat
                            )
                                return

                            if (isQuoteTotalOutOfRange) {
                                setFormError(tKey('quoteTotalOutOfRange'))
                                return
                            }

                            setFormError(null)
                            upsertPricing()
                        }}
                    />

                </DialogFooter>
            </Dialog>
        </div>
    )
}
